import config from "../config";  // load global config first
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { loadAuthedUser } from "../utils/authedUser";
import { loadAnonToken, saveAnonToken } from "../utils/anonToken";

const backendBaseURLs = new Map([
  ["local", "http://localhost:8080"],
  ["dev", "https://alb-dev.askthesea.com"],
  ["prod", "https://alb-prod.askthesea.com"]
]);

export const baseURL = backendBaseURLs.get(config.AppEnv);
console.log("baseURL: " + baseURL);

// Create an axios instance
const api = axios.create({
  baseURL: backendBaseURLs.get(config.AppEnv), // change to your API base URL
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000, // optional: 10 seconds
});

// isOwnBackendRequest reports whether `url` actually targets our own backend, so the
// interceptor below never attaches the Bearer token to anything else.
//
// Every call site in this app builds `url` as `${baseURL}/...` (an absolute URL), so a plain
// prefix check is enough for those. Relative URLs are included too since axios always resolves
// them against this instance's own `baseURL` before sending. That leaves out exactly the cases
// that matter: a pre-signed S3 URL (absolute, points at *.s3.*.amazonaws.com, previously
// special-cased by name via the "X-Amz-Algorithm" query param it happens to carry) and, more
// importantly, any absolute URL some future call site might pass in that points anywhere else
// (a third-party webhook, a CDN link, etc.) — without this check the old logic would have
// attached this user's auth token to a request to that address too.
function isOwnBackendRequest(url) {
  if (!url) return false;
  if (!/^https?:\/\//i.test(url)) return true; // relative URL: always resolves against baseURL
  return url.startsWith(baseURL);
}

// Add a request interceptor (e.g., attach token)
api.interceptors.request.use(
  (config) => {
    // Generate a unique request ID
    const requestId = uuidv4();

    // Add it to headers
    config.headers["X-Request-ID"] = requestId;

    if (isOwnBackendRequest(config.url)) {
      // attach auth token from local storage
      const authedUser = loadAuthedUser();
      if (authedUser) {
        config.headers.Authorization = `Bearer ${authedUser.userToken}`;
      } else {
        // No real login — fall back to the server-minted anonymous session token (if we have
        // one yet), so "Ask The Sea" requests carry it the same way a real user's token rides.
        const anonToken = loadAnonToken();
        if (anonToken) {
          config.headers.Authorization = `Bearer ${anonToken}`;
        }
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// --- Why this 401 interceptor lives here, at module scope, instead of inside AuthProvider ---
//
// This used to be registered by AuthProvider (utils/authContext.js) inside a useEffect, via
// api.interceptors.response.use(...). That caused a real bug: on the very first page load, a
// logged-in-but-revoked user would NOT get auto-logged-out when the backend correctly rejected
// their token with 401.
//
// Root cause (a race, not a logic bug in the 401 check itself): axios builds a request's
// response-handling chain SYNCHRONOUSLY at the moment .get()/.post() is called, using whatever
// interceptors are registered on the instance at that exact instant. Interceptors added later do
// NOT retroactively attach to a request that was already dispatched.
//
// On mount, React fires child components' useEffects BEFORE their parents' — that's just how
// React's effect commit ordering works (bottom-up). AuthProvider is an ancestor of most of the
// app, including TheSeaContext (utils/../Components/the-sea/TheSeaContext.js), which fires an
// api.get(".../authenticated/point/balance") call in its own mount effect. Because that's a
// child of AuthProvider, its effect — and therefore its api.get() call — runs BEFORE
// AuthProvider's own effect gets to call api.interceptors.response.use(...). So that very first
// authenticated request's promise chain got built with ZERO interceptors attached. Its 401 only
// ever reached the component's own local .catch(), never a global "log the user out" handler.
// And since nothing else naturally re-fires that request afterward, the stale "logged in" state
// just stuck around for the rest of the session.
//
// Fix: register the interceptor unconditionally right here, at module load time — i.e. the
// moment this file is first imported, before React has rendered a single component. That
// guarantees the interceptor exists before ANY component (parent or child) can possibly call
// api.get()/api.post(), so no request's chain can ever be built without it.
//
// The interceptor itself can't call logout()/navigate("/") directly (this is a plain JS module,
// not a React component — it has no access to React state or the router). So instead it just
// calls whatever function AuthProvider has plugged into `unauthorizedHandler` via
// setUnauthorizedHandler() below. AuthProvider sets that callback in its own useEffect, which
// (thanks to the same child-before-parent ordering) runs slightly AFTER this module's top-level
// code but still synchronously within the same initial render pass — well before any network
// response could possibly come back and need handling. So by the time a 401 actually arrives,
// the handler is always already wired up.
let unauthorizedHandler = null;
export const setUnauthorizedHandler = (handler) => {
  unauthorizedHandler = handler;
};

api.interceptors.response.use(
  (response) => {
    // The two /anonymous/copilot/* endpoints mint a fresh anonymous session token whenever the
    // caller presented none — persist it here, in one place, rather than in every call site.
    if (response?.data?.anonToken) {
      saveAnonToken(response.data.anonToken);
    }
    return response;
  },
  (error) => {
    // Only treat a 401 as "the backend rejected our existing session" if we actually had a
    // logged-in session to begin with. Without this check, a 401 from a public/anonymous
    // endpoint that failed for an unrelated reason — e.g. native_login returning 401 for
    // "invalid email or password" — would trigger a global logout+redirect-home before the
    // caller's own .catch() ever gets to show that error on the form.
    if (error.response && error.response.status === 401 && unauthorizedHandler && loadAuthedUser()) {
      unauthorizedHandler();
    }

    if (error.response) {
      console.error("API error:", error.response.data);
    } else {
      console.error("Network error:", error.message);
    }

    return Promise.reject(error);
  }
);

export default api;
